import axios from 'axios';
import { API_CONFIG } from '../config/env';

export interface PaymentLinkRequest {
  fiatCurrency: string;
  fiatAmount: number;
  userId: string;
  title?: string;
  description?: string;
}

export interface GenerateLinkRequest {
  userId: number;
  fiatCurrency: string;
  amount: number;
}

export interface GenerateLinkResponse {
  transactionId: number;
  paymentLinks: string[];
  uniqueWalletAddress: string;
}

export interface PaymentLinkResponse {
  uniqueHash: string;
  paymentUrl: string;
  createdAt: string;
  expiresAt?: string;
  status: 'active' | 'inactive' | 'expired';
}

export interface PaymentLink {
  id: string;
  uniqueHash: string;
  paymentUrl: string;
  fiatCurrency: string;
  fiatAmount: number;
  userId: string;
  userName: string;
  userRole: string;
  title?: string;
  description?: string;
  status: 'active' | 'inactive' | 'expired';
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  clickCount: number;
  conversionCount: number;
}

class PaymentLinkService {
  private extractApiErrorMessage(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') return null;
    const obj = payload as Record<string, unknown>;

    const expandedError =
      obj.expandedError && typeof obj.expandedError === 'object'
        ? (obj.expandedError as Record<string, unknown>)
        : null;

    const candidates: unknown[] = [
      expandedError?.technicalMessage,
      expandedError?.message,
      obj.technicalMessage,
      obj.message,
      obj.error,
      obj.title,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }
    return null;
  }

  private toAbsoluteUrl(link: string): string {
    if (!link) return link;
    if (/^https?:\/\//i.test(link)) return link;
    const normalizedPath = link.startsWith('/') ? link : `/${link}`;
    return `${window.location.origin}${normalizedPath}`;
  }

  private unwrapPayload<T>(payload: unknown): T | null {
    if (payload === null || payload === undefined) return null;
    if (typeof payload !== 'object') return payload as T;

    const obj = payload as Record<string, unknown>;
    const candidates = ['data', 'result', 'value'];
    for (const key of candidates) {
      if (obj[key] !== undefined && obj[key] !== null) {
        return obj[key] as T;
      }
    }
    return payload as T;
  }

  private normalizeGenerateLinkResponse(payload: unknown): GenerateLinkResponse {
    if (payload && typeof payload === 'object') {
      const wrapper = payload as Record<string, unknown>;
      if (typeof wrapper.success === 'boolean' && wrapper.success === false) {
        const msg =
          this.extractApiErrorMessage(wrapper) ??
          'Failed to generate payment links.';
        throw new Error(msg);
      }
    }

    const base = this.unwrapPayload<Record<string, unknown>>(payload) ?? {};
    const rawLinks = Array.isArray(base.paymentLinks) ? (base.paymentLinks as unknown[]) : [];
    const paymentLinks = rawLinks
      .filter((link): link is string => typeof link === 'string' && link.trim().length > 0)
      .map((link) => this.toAbsoluteUrl(link.trim()));

    return {
      transactionId: Number(base.transactionId ?? 0),
      paymentLinks,
      uniqueWalletAddress: typeof base.uniqueWalletAddress === 'string' ? base.uniqueWalletAddress : '',
    };
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Create a new payment link
   */
  async createPaymentLink(data: PaymentLinkRequest): Promise<PaymentLinkResponse> {
    try {
      const response = await axios.post<PaymentLinkResponse>(`${API_CONFIG.BASE_URL}/payment-links`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating payment link:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Get all payment links for the current user
   */
  async getPaymentLinks(): Promise<PaymentLink[]> {
    try {
      const response = await axios.get<PaymentLink[]>(`${API_CONFIG.BASE_URL}/payment-links`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching payment links:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Get a specific payment link by unique hash
   */
  async getPaymentLink(uniqueHash: string): Promise<PaymentLink> {
    try {
      const response = await axios.get<PaymentLink>(`${API_CONFIG.BASE_URL}/payment-links/${uniqueHash}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching payment link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Update a payment link
   */
  async updatePaymentLink(uniqueHash: string, data: Partial<PaymentLinkRequest>): Promise<PaymentLink> {
    try {
      const response = await axios.put<PaymentLink>(`${API_CONFIG.BASE_URL}/payment-links/${uniqueHash}`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating payment link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Delete a payment link
   */
  async deletePaymentLink(uniqueHash: string): Promise<void> {
    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/payment-links/${uniqueHash}`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error(`Error deleting payment link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Get payment link analytics
   */
  async getPaymentLinkAnalytics(uniqueHash: string): Promise<{
    clickCount: number;
    conversionCount: number;
    conversionRate: number;
    totalRevenue: number;
    lastClickedAt?: string;
  }> {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/payment-links/${uniqueHash}/analytics`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching analytics for payment link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Generate payment links using the new endpoint
   */
  async generatePaymentLinks(data: GenerateLinkRequest): Promise<GenerateLinkResponse> {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/Transactions/generate_link`, data, {
        headers: this.getAuthHeaders(),
      });
      const body = this.unwrapPayload<unknown>(response.data) ?? response.data;
      if (body && typeof body === 'object' && (body as { success?: boolean }).success === false) {
        const msg = this.extractApiErrorMessage(response.data);
        throw new Error(msg || 'Payment link generation failed.');
      }
      return this.normalizeGenerateLinkResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg = this.extractApiErrorMessage(error.response?.data);
        if (msg) throw new Error(msg);
      }
      throw error;
    }
  }

  /**
   * Generate a mock payment link for testing
   */
  generateMockPaymentLink(data: PaymentLinkRequest): PaymentLinkResponse {
    const uniqueHash = `PAY_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const paymentUrl = `${window.location.origin}/pay/${uniqueHash}`;
    
    return {
      uniqueHash,
      paymentUrl,
      createdAt: new Date().toISOString(),
      status: 'active'
    };
  }
}

export const paymentLinkService = new PaymentLinkService();
